/**
 * Canonical age group definitions for Swimmingly.
 *
 * This is the single source of truth for:
 *   - ageGroupArr       (numeric index → display string, used in event/meet dropdowns)
 *   - meetAgeGroupArr   (same but "" key for Select, no duplicate at index 38)
 *   - SD3_AGE_GROUP_TO_DB (SD3 4-char code → display string, used when importing SD3 files)
 *
 * To add a new age group:
 *   1. Append an entry here with the next sequential index.
 *   2. Include the `sd3` field if the group appears in Hy-Tek SD3 export files.
 *   3. Update the server-side equivalent: helpers/sd3AgeGroups.js (SD3 entries only).
 */

export interface AgeGroupDef {
  /** Numeric key used in ageGroupArr (0 = "Select"). */
  index: number
  /** Human-readable label stored in event.AgeGroup and meet_event_orders.age_group. */
  display: string
  /**
   * 4-character Hy-Tek SD3 age-group code, present when this group appears in SD3 files.
   * Used to derive SD3_AGE_GROUP_TO_DB.
   */
  sd3?: string
  /**
   * True for the historical duplicate "12 & Under" at index 38.
   * Excluded from meetAgeGroupArr; present in ageGroupArr for backward compatibility.
   */
  isDuplicate?: boolean
}

export const AGE_GROUP_DEFINITIONS: ReadonlyArray<AgeGroupDef> = [
  { index: 0,  display: "Select" },
  { index: 1,  display: "Open",         sd3: "UNOV" },
  { index: 2,  display: "3",            sd3: "0303" },
  { index: 3,  display: "4",            sd3: "0404" },
  { index: 4,  display: "5",            sd3: "0505" },
  { index: 5,  display: "5-18",         sd3: "0518" },
  { index: 6,  display: "5-6",          sd3: "0506" },
  { index: 7,  display: "6",            sd3: "0606" },
  { index: 8,  display: "6 & Under",    sd3: "UN06" },
  { index: 9,  display: "6-7",          sd3: "0607" },
  { index: 10, display: "6-8",          sd3: "0608" },
  { index: 11, display: "7",            sd3: "0707" },
  { index: 12, display: "7 & Over",     sd3: "07OV" },
  { index: 13, display: "7 & Under",    sd3: "UN07" },
  { index: 14, display: "7-12",         sd3: "0712" },
  { index: 15, display: "7-8",          sd3: "0708" },
  { index: 16, display: "7-9",          sd3: "0709" },
  { index: 17, display: "8",            sd3: "0808" },
  { index: 18, display: "8 & Under",    sd3: "UN08" },
  { index: 19, display: "8-11",         sd3: "0811" },
  { index: 20, display: "8-9",          sd3: "0809" },
  { index: 21, display: "9",            sd3: "0909" },
  { index: 22, display: "9-10",         sd3: "0910" },
  { index: 23, display: "9-18",         sd3: "0918" },
  { index: 24, display: "10",           sd3: "1010" },
  { index: 25, display: "10 & Over",    sd3: "10OV" },
  { index: 26, display: "10 & Under",   sd3: "UN10" },
  { index: 27, display: "10-11",        sd3: "1011" },
  { index: 28, display: "10-12",        sd3: "1012" },
  { index: 29, display: "11",           sd3: "1111" },
  { index: 30, display: "11 & Over",    sd3: "11OV" },
  { index: 31, display: "11 & Under",   sd3: "UN11" },
  { index: 32, display: "11-12",        sd3: "1112" },
  { index: 33, display: "11-13",        sd3: "1113" },
  { index: 34, display: "11-14",        sd3: "1114" },
  { index: 35, display: "12",           sd3: "1212" },
  { index: 36, display: "12 & Over",    sd3: "12OV" },
  { index: 37, display: "12 & Under",   sd3: "UN12" },
  // Historical duplicate — kept so ageGroupArr[38] resolves to a value;
  // excluded from meetAgeGroupArr (which has no gap at 38).
  { index: 38, display: "12 & Under",   isDuplicate: true },
  { index: 39, display: "12-13",        sd3: "1213" },
  { index: 40, display: "12-15",        sd3: "1215" },
  { index: 41, display: "13",           sd3: "1313" },
  { index: 42, display: "13 & Over",    sd3: "13OV" },
  { index: 43, display: "13 & Under",   sd3: "UN13" },
  { index: 44, display: "13-14",        sd3: "1314" },
  { index: 45, display: "13-15",        sd3: "1315" },
  { index: 46, display: "13-16",        sd3: "1316" },
  { index: 47, display: "13-18",        sd3: "1318" },
  { index: 48, display: "14",           sd3: "1414" },
  { index: 49, display: "14 & Over",    sd3: "14OV" },
  { index: 50, display: "14 & Under",   sd3: "UN14" },
  { index: 51, display: "14-15",        sd3: "1415" },
  { index: 52, display: "14-18",        sd3: "1418" },
  { index: 53, display: "15",           sd3: "1515" },
  { index: 54, display: "15 & Over",    sd3: "15OV" },
  { index: 55, display: "15-16",        sd3: "1516" },
  { index: 56, display: "15-17",        sd3: "1517" },
  { index: 57, display: "15-18",        sd3: "1518" },
  { index: 58, display: "16",           sd3: "1616" },
  { index: 59, display: "16 & Over",    sd3: "16OV" },
  { index: 60, display: "16-17",        sd3: "1617" },
  { index: 61, display: "16-20",        sd3: "1620" },
  { index: 62, display: "17",           sd3: "1717" },
  { index: 63, display: "17 & Under",   sd3: "UN17" },
  { index: 64, display: "17-18",        sd3: "1718" },
  { index: 65, display: "17-19",        sd3: "1719" },
  { index: 66, display: "17 & Over",    sd3: "17OV" },
  { index: 67, display: "18",           sd3: "1818" },
  { index: 68, display: "18 & Over",    sd3: "18OV" },
  { index: 69, display: "18 & Under",   sd3: "UN18" },
  { index: 70, display: "18-19",        sd3: "1819" },
  { index: 71, display: "19-25",        sd3: "1925" },
  { index: 72, display: "19-29",        sd3: "1929" },
  { index: 73, display: "20 & Over",    sd3: "20OV" },
  { index: 74, display: "21 & Over",    sd3: "21OV" },
  { index: 75, display: "26-35",        sd3: "2635" },
  { index: 76, display: "30-39",        sd3: "3039" },
  { index: 77, display: "36-50",        sd3: "3650" },
  { index: 78, display: "40-49",        sd3: "4049" },
  { index: 79, display: "50-59",        sd3: "5059" },
  { index: 80, display: "51-64",        sd3: "5164" },
  { index: 81, display: "60-69",        sd3: "6069" },
  { index: 82, display: "65 & Over",    sd3: "65OV" },
  { index: 83, display: "70 & Over",    sd3: "70OV" },
  { index: 84, display: "70-79",        sd3: "7079" },
  { index: 85, display: "80 & Over",    sd3: "80OV" },
]

/**
 * Integer-keyed map from age group index to display label.
 * Used in event/meet creation dropdowns.
 * Index 0 = "Select"; index 38 is a historical duplicate of 37 ("12 & Under").
 */
export const ageGroupArr: { [key: number]: string } = Object.fromEntries(
  AGE_GROUP_DEFINITIONS.map((d) => [d.index, d.display]),
)

/**
 * Same display labels as ageGroupArr but:
 *   - uses `""` as the key for "Select" instead of `0`
 *   - omits the duplicate "12 & Under" at index 38, so keys 38–84 map to the
 *     entries that are at keys 39–85 in ageGroupArr.
 */
export const meetAgeGroupArr: { [key: string]: string } = (() => {
  const entries: Array<[string | number, string]> = [["", "Select"]]
  let meetIdx = 1
  for (const d of AGE_GROUP_DEFINITIONS) {
    if (d.index === 0) continue     // "Select" handled above with "" key
    if (d.isDuplicate) continue     // skip historical duplicate at index 38
    entries.push([meetIdx, d.display])
    meetIdx++
  }
  return Object.fromEntries(entries)
})()

/**
 * Maps a 4-character Hy-Tek SD3 age-group code to its display label.
 * Used when importing SD3 files to normalise age group strings before DB writes.
 *
 * Derived from AGE_GROUP_DEFINITIONS entries that carry an `sd3` field.
 *
 * Server-side equivalent (must be kept in sync): helpers/sd3AgeGroups.js
 */
export const SD3_AGE_GROUP_TO_DB: Record<string, string> = Object.fromEntries(
  AGE_GROUP_DEFINITIONS.filter(
    (d): d is AgeGroupDef & { sd3: string } => !!d.sd3 && !d.isDuplicate,
  ).map((d) => [d.sd3, d.display]),
)
